import { absoluteUrl } from '@/lib/seo/siteUrl';

/**
 * Single source of truth for supported app locales (navbar, routing, flags, RTL, SEO).
 */
export const APP_LOCALES = [
  'en',
  'ar',
  'es',
  'zh-CN',
  'hi',
  'fr',
  'pt-BR',
  'de',
  'ja',
  'ko',
  'ru',
  'id',
  'it',
  'tr',
  'vi',
  'th',
  'pl',
  'nl',
  'uk',
  'bn',
  'ta',
  'ur',
] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'en';

export type LocaleMeta = {
  /** BCP-47 tag used in URLs and next-intl */
  id: AppLocale;
  /** Flag SVG basename under `public/assets/images/flags/` */
  flagCode: string;
  /** Open Graph locale (e.g. `es_ES`) */
  ogLocale: string;
  /** Google Translate API language code */
  translateCode: string;
  rtl?: boolean;
};

export const LOCALE_META: Record<AppLocale, LocaleMeta> = {
  en: { id: 'en', flagCode: 'EN-US', ogLocale: 'en_US', translateCode: 'en' },
  ar: { id: 'ar', flagCode: 'SA', ogLocale: 'ar_SA', translateCode: 'ar', rtl: true },
  es: { id: 'es', flagCode: 'ES', ogLocale: 'es_ES', translateCode: 'es' },
  'zh-CN': { id: 'zh-CN', flagCode: 'CN', ogLocale: 'zh_CN', translateCode: 'zh-CN' },
  hi: { id: 'hi', flagCode: 'IN', ogLocale: 'hi_IN', translateCode: 'hi' },
  fr: { id: 'fr', flagCode: 'FR', ogLocale: 'fr_FR', translateCode: 'fr' },
  'pt-BR': { id: 'pt-BR', flagCode: 'BR', ogLocale: 'pt_BR', translateCode: 'pt' },
  de: { id: 'de', flagCode: 'DE', ogLocale: 'de_DE', translateCode: 'de' },
  ja: { id: 'ja', flagCode: 'JP', ogLocale: 'ja_JP', translateCode: 'ja' },
  ko: { id: 'ko', flagCode: 'KR', ogLocale: 'ko_KR', translateCode: 'ko' },
  ru: { id: 'ru', flagCode: 'RU', ogLocale: 'ru_RU', translateCode: 'ru' },
  id: { id: 'id', flagCode: 'ID', ogLocale: 'id_ID', translateCode: 'id' },
  it: { id: 'it', flagCode: 'IT', ogLocale: 'it_IT', translateCode: 'it' },
  tr: { id: 'tr', flagCode: 'TR', ogLocale: 'tr_TR', translateCode: 'tr' },
  vi: { id: 'vi', flagCode: 'VN', ogLocale: 'vi_VN', translateCode: 'vi' },
  th: { id: 'th', flagCode: 'TH', ogLocale: 'th_TH', translateCode: 'th' },
  pl: { id: 'pl', flagCode: 'PL', ogLocale: 'pl_PL', translateCode: 'pl' },
  nl: { id: 'nl', flagCode: 'NL', ogLocale: 'nl_NL', translateCode: 'nl' },
  uk: { id: 'uk', flagCode: 'UK', ogLocale: 'uk_UA', translateCode: 'uk' },
  bn: { id: 'bn', flagCode: 'BD', ogLocale: 'bn_BD', translateCode: 'bn' },
  ta: { id: 'ta', flagCode: 'LK', ogLocale: 'ta_LK', translateCode: 'ta' },
  ur: { id: 'ur', flagCode: 'PK', ogLocale: 'ur_PK', translateCode: 'ur', rtl: true },
};

/** Native language names for the locale switcher (same in every locale file). */
export const LOCALE_SWITCHER_LABELS: Record<AppLocale, string> = {
  en: 'English',
  ar: 'العربية',
  es: 'Español',
  'zh-CN': '简体中文',
  hi: 'हिन्दी',
  fr: 'Français',
  'pt-BR': 'Português (Brasil)',
  de: 'Deutsch',
  ja: '日本語',
  ko: '한국어',
  ru: 'Русский',
  id: 'Bahasa Indonesia',
  it: 'Italiano',
  tr: 'Türkçe',
  vi: 'Tiếng Việt',
  th: 'ไทย',
  pl: 'Polski',
  nl: 'Nederlands',
  uk: 'Українська',
  bn: 'বাংলা',
  ta: 'தமிழ்',
  ur: 'اردو',
};

export const RTL_LOCALES: ReadonlySet<string> = new Set(
  APP_LOCALES.filter((loc) => LOCALE_META[loc].rtl),
);

export function localeFlagPath(locale: string): string | null {
  const meta = LOCALE_META[locale as AppLocale];
  if (!meta) return null;
  return `/assets/images/flags/${meta.flagCode}.svg`;
}

export function openGraphLocale(locale: string): string {
  return LOCALE_META[locale as AppLocale]?.ogLocale ?? 'en_US';
}

export function schemaInLanguage(locale: string): string {
  if (locale === 'ar') return 'ar';
  if (locale === 'en') return 'en';
  return locale;
}

export function alternateLanguageUrls(pathWithoutLocale: string): Record<string, string> {
  const normalized = pathWithoutLocale.replace(/^\//, '').replace(/\/$/, '');
  const pathSuffix = normalized ? `/${normalized}` : '';
  const links: Record<string, string> = {};
  for (const loc of APP_LOCALES) {
    links[loc] = absoluteUrl(`/${loc}${pathSuffix ? `${pathSuffix}` : '/'}`);
  }
  links['x-default'] = links.en;
  return links;
}
